import React from 'react';
import { Helmet } from 'react-helmet-async';
import { cloudImg } from '../config/images';

const SEO = ({
  title = "Near the Beach - Beachfront Villas in Kannur, Kerala",
  description = "Near the Beach curates handpicked beachfront villas on Kannur's coast, starting with Grand Beach Villa on Payyambalam Beach. Discover pristine beaches, local culture, and exceptional hospitality.",
  keywords = "Near the Beach, Kannur beach villas, where to stay Kannur, Kerala beach accommodation, Grand Beach Villa, Payyambalam beach stay, Muzhappilangad beach resort, Kerala tourism, Arabian Sea accommodation, things to do in Kannur, best beaches in Kannur, family vacation rental, beach villa, beach destination, vacation rental Kerala, beachfront accommodation, seaside resort, coastal villa, Kerala beach house, beachside retreat",
  image = cloudImg('home_page_banner.jpg'),
  url = "https://nearthebeach.in/",
  type = "website",
  faqItems = null,
  includeLodgingSchema = false
}) => {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Near the Beach",
      "url": "https://nearthebeach.in/",
      "description": "Discover the beautiful beaches of Kannur, Kerala and book your stay at a handpicked beachfront villa.",
      "publisher": {
        "@type": "Organization",
        "name": "Near the Beach",
        "logo": {
          "@type": "ImageObject",
          "url": cloudImg('logo-grandbeachvilla.png')
        }
      }
    }
  ];

  if (includeLodgingSchema) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      "name": "Grand Beach Villa",
      "description": description,
      "image": image,
      "url": url,
      "telephone": "+919895162332",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Payyambalam Beach Road",
        "addressLocality": "Kannur",
        "addressRegion": "Kerala",
        "postalCode": "670001",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "11.8745",
        "longitude": "75.3704"
      },
      "priceRange": "$$",
      "amenityFeature": [
        {
          "@type": "LocationFeatureSpecification",
          "name": "Beach Access",
          "value": "Direct beach access"
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Accommodation Type",
          "value": "Luxury Beach Villa"
        }
      ],
      "nearbyAttraction": [
        {
          "@type": "Beach",
          "name": "Payyambalam Beach",
          "description": "One of the most popular beaches in Kannur, stretching for about 10 kilometers along the coast."
        },
        {
          "@type": "Beach",
          "name": "Muzhappilangad Drive-in Beach",
          "description": "Asia's longest drive-in beach, where you can drive your vehicle directly on the beach."
        }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "78"
      },
      "starRating": {
        "@type": "Rating",
        "ratingValue": "5"
      },
      "review": [
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Anoushka Girish" },
          "datePublished": "2026-04-15",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "reviewBody": "The stay was absolutely fantastic. We really appreciated the sense of privacy, and everything felt so thoughtfully put together. The location, the view, the service, and the overall setup were all wonderful."
        },
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Sudhanshu Tripathi" },
          "datePublished": "2026-05-15",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "reviewBody": "Stayed for one night. The rooms are good, and well maintained. Clean. Plus it is located just 50 metres from the beach so it's very convenient to visit any time."
        },
        {
          "@type": "Review",
          "author": { "@type": "Person", "name": "Raj" },
          "datePublished": "2026-01-18",
          "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
          "reviewBody": "The property feels almost new, and everything is spotlessly clean and very well maintained. It is located just opposite the beach, about 50 metres away."
        }
      ]
    });
  }

  if (faqItems && faqItems.length > 0) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map((item) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    });
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="Near the Beach" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Near the Beach" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#2c5530" />
      <meta name="msapplication-TileColor" content="#2c5530" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
